import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Checkbox, Form, Input, Modal, Popconfirm, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

import { CLAIMS, CLAIM_GROUP_LABEL, claimText } from '@lfoc/domain/authority/claims.js';

import { useAccess } from '../../auth/AccessContext.jsx';
import ActionIcon from '../../components/ActionIcon.jsx';
import { useLiveTopic } from '../../live/LiveContext.js';
import { addNewlyCovered, keepCovered, offeredByGroup, subscriptionLabels } from './roleNotifications.js';

const { Text } = Typography;

/**
 * Roles are sets of claims (IP-46). A claim is a permission the server
 * enforces, fixed in code; a role is data an administrator builds from them. A
 * person's permissions are the union of their roles' claims. An administrator
 * never edits or retires a role they hold - the server refuses it, and this
 * page says so rather than hiding the role.
 *
 * A role also subscribes to NOTIFICATIONS (docs/notifications/implementation-
 * guide.md 8.2, migration 0024): the dialog's Notifications tab, beside
 * the Permissions tab, offers only the notifications the ticked permissions cover
 * (roleNotifications.js), ticks the related notifications when a permission is
 * ticked, and unticks one when the permission that covered it is unticked -
 * the server refuses anything else. A person then receives a
 * notification when one of their roles subscribes to it.
 */
const RolesPanel = () => {
    const { api, principal } = useAccess();
    const { message } = App.useApp();
    const [roles, setRoles] = useState([]);
    const [claims, setClaims] = useState([]);
    // The notification catalogue (GET /access/notifications); empty if the server predates 0024.
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null); // a role, or { isNew: true }
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState(null);
    const [openGroups, setOpenGroups] = useState(() => new Set());
    const [openNotificationGroups, setOpenNotificationGroups] = useState(() => new Set());
    const notificationGroupsPrimed = useRef(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [{ roles: defs }, { claims: catalogue }, notificationList] = await Promise.all([
                api.access.roles(),
                api.access.claims(),
                // Not fatal: without the catalogue the dialog simply shows no Notifications section.
                api.access.notifications().catch(() => ({ notifications: [] }))
            ]);
            setRoles(defs);
            setClaims(catalogue);
            setNotifications(notificationList.notifications ?? []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [api]);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // another administrator saved or retired a role.
    useLiveTopic(['access'], load);

    useEffect(() => {
        load();
    }, [load]);

    // Grouped by Console section, in the catalogue's order (claims.js), each
    // claim with its plain-English name and what it lets a person do.
    const byGroup = useMemo(() => {
        const order = new Map(CLAIMS.map((c, i) => [c.claim, i]));
        const groups = new Map();
        for (const c of [...claims].sort((a, b) => (order.get(a.claim) ?? 99) - (order.get(b.claim) ?? 99))) {
            const group = c.group ?? CLAIM_GROUP_LABEL[c.screen] ?? c.screen;
            if (!groups.has(group)) groups.set(group, []);
            groups.get(group).push({ ...claimText(c.claim), ...c });
        }
        return [...groups.entries()];
    }, [claims]);

    const held = (role) => principal.roles.includes(role);

    // The claims ticked in the dialog right now, so the Notifications section
    // follows them as the administrator ticks and unticks.
    const tickedClaims = Form.useWatch('claims', form) ?? [];
    // The notifications ticked right now, for the count on the Notifications tab.
    const tickedNotifications = Form.useWatch('notifications', form) ?? [];
    const offered = useMemo(() => offeredByGroup(notifications, tickedClaims), [notifications, tickedClaims]);

    // The claims as they were before the latest change. onValuesChange only
    // hands over the new value, and auto-ticking needs to know which claims
    // were JUST ticked, so the previous set is kept here (reset by open()).
    const prevClaims = useRef([]);

    // The permissions changed. Two things follow, in this order:
    //   1. a permission was ticked: tick the notifications it covers, so the
    //      administrator gets the related notifications without hunting for
    //      them (they can still untick any of them on the Notifications tab);
    //   2. a permission was unticked: untick the notifications it covered, as
    //      the server would drop them (authority/access.js saveRole).
    const onValuesChange = (changed, all) => {
        if (!('claims' in changed)) return;
        const added = addNewlyCovered(all.notifications, notifications, prevClaims.current, all.claims);
        const kept = keepCovered(added, notifications, all.claims);
        prevClaims.current = all.claims ?? [];
        // Only touch the field when the set really changed, to avoid a needless re-render.
        const current = [...(all.notifications ?? [])].sort();
        if (kept.join('|') !== current.join('|')) form.setFieldsValue({ notifications: kept });
    };

    const open = (role) => {
        setDialogError(null);
        setEditing(role ?? { isNew: true });
        setOpenGroups(new Set(byGroup[0] ? [byGroup[0][0]] : []));
        const notificationGroups = offeredByGroup(notifications, role?.claims ?? []);
        setOpenNotificationGroups(new Set(notificationGroups[0] ? [notificationGroups[0][0]] : []));
        notificationGroupsPrimed.current = notificationGroups.length > 0;
        // The role's saved claims are the starting point: only claims ticked from here on auto-tick notifications.
        prevClaims.current = role?.claims ?? [];
        form.setFieldsValue(role
            ? { role: role.role, name: role.name, description: role.description, claims: role.claims, notifications: role.notifications ?? [] }
            : { role: '', name: '', description: '', claims: [], notifications: [] });
    };


    useEffect(() => {
        if (!editing) return;
        if (offered.length === 0) {
            notificationGroupsPrimed.current = false;
            return;
        }
        if (notificationGroupsPrimed.current) return;
        notificationGroupsPrimed.current = true;
        setOpenNotificationGroups(new Set([offered[0][0]]));
    }, [editing, offered]);

    const toggleGroup = (group, setter) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(group)) next.delete(group);
            else next.add(group);
            return next;
        });
    };

    const save = async (values) => {
        setBusy(true);
        setDialogError(null);
        try {
            await api.access.saveRole({
                role: values.role.trim().toUpperCase(),
                name: values.name.trim(),
                description: values.description?.trim() || null,
                claims: values.claims ?? [],
                // The whole set the role subscribes to; the server checks each against the claims.
                notifications: keepCovered(values.notifications, notifications, values.claims)
            });
            message.success('Role ' + values.role.trim().toUpperCase() + ' saved. It applies to its holders from their next request.');
            setEditing(null);
            await load();
        } catch (e) {
            setDialogError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const retire = async (role) => {
        try {
            await api.access.retireRole(role.role);
            message.success('Role ' + role.role + ' retired.');
            await load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: 'Role',
            key: 'role',
            width: 190,
            render: (_, r) => (
                <>
                    <div className="role-name-line">
                        <Text strong>{r.name}</Text>
                        {r.description && (
                            <Tooltip title={r.description}>
                                <span className="role-description-info"><ActionIcon name="info" /></span>
                            </Tooltip>
                        )}
                    </div>
                    <div className="role-meta-line">
                        <Text type="secondary">({r.role})</Text>
                        {r.isSystem && <Tag>system</Tag>}
                        {r.retiredAt && <Tag color="default">retired</Tag>}
                    </div>
                </>
            )
        },
        {
            title: 'Permissions',
            dataIndex: 'claims',
            key: 'claims',
            width: 500,
            render: (list) => (list.length
                ? <span className="permission-summary">{list.map((c) => claimText(c).label).join(' · ')}</span>
                : <Text type="secondary">none</Text>)
        },
        {
            title: 'Notifications (Subscribed)',
            dataIndex: 'notifications',
            key: 'notifications',
            className: 'role-notifications-cell',
            width: 175,
            render: (list) => (list?.length
                ? <Tooltip title={subscriptionLabels(list, notifications).join(' · ')}><Text>{list.length}</Text></Tooltip>
                : <Text type="secondary">0</Text>)
        },
        { title: 'Holders', dataIndex: 'holders', key: 'holders', width: 70 },
        {
            title: '',
            key: 'actions',
            width: 225,
            render: (_, r) => {
                if (r.retiredAt) return null;
                if (held(r.role)) return <Tooltip title="You hold this role, so another administrator must change it."><Text type="secondary">You hold it</Text></Tooltip>;
                return (
                    <Space>
                        <Button className="role-action-button permission-edit-button" icon={<ActionIcon name="edit" />} onClick={() => open(r)}>Edit</Button>
                        <Popconfirm title={'Retire ' + r.role + '?'} description="Nobody may hold it. It is kept, because the audit trail names it."
                            disabled={r.holders > 0} onConfirm={() => retire(r)}>
                            <Tooltip title={r.holders > 0 ? 'Remove it from its holders first.' : ''}>
                                <Button className="role-action-button" icon={<ActionIcon name="retire" disabled={r.holders > 0} />}
                                    danger disabled={r.holders > 0}>Retire</Button>
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            }
        }
    ];

    return (
        <section className="panel permission-panel roles-panel">
            <div className="permission-toolbar">
                <div className="permission-panel-heading roles-panel-heading">
                    <h3 className="panel-title">Roles</h3>
                    <div className="permission-info-banner">
                        <ActionIcon name="info" />
                        <span>A role is a set of permissions. A person can do what any of their roles allows, and maker-checker rules still apply to every record.</span>
                    </div>
                </div>
                <Space className="permission-toolbar-actions">
                    <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={load} loading={loading}>Refresh</Button>
                    <Button type="primary" icon={<ActionIcon name="addRole" />} onClick={() => open(null)}>Add Role</Button>
                </Space>
            </div>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
            <Table className="permission-table roles-table" rowKey="role" size="small" loading={loading} columns={columns}
                dataSource={loading && roles.length === 0 ? undefined : roles} tableLayout="fixed" scroll={{ x: 980 }}
                pagination={false} locale={{ emptyText: 'No roles yet' }} />

            <Modal open={Boolean(editing)} width={720} title={editing?.isNew ? 'New role' : 'Edit ' + (editing?.role ?? '')}
                onCancel={() => setEditing(null)} onOk={() => form.submit()} okText="Save" okButtonProps={{ loading: busy }}
                rootClassName="lfoc-styles-wrapper role-edit-modal" destroyOnHidden>
                {dialogError && <Alert type="error" showIcon message={dialogError} className="auth-alert" />}
                <Form form={form} layout="vertical" requiredMark={false} onFinish={save} onValuesChange={onValuesChange}>
                    <Form.Item name="role" label="Code" rules={[
                        { required: true, message: 'A code is required.' },
                        { pattern: /^[A-Za-z][A-Za-z0-9_]{1,39}$/, message: 'Letters, digits and _, starting with a letter; 2-40 characters.' }
                    ]}>
                        <Input disabled={!editing?.isNew} placeholder="CASH_DESK" />
                    </Form.Item>
                    <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'A name is required.' }, { max: 80 }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Description"><Input.TextArea rows={2} maxLength={1000} /></Form.Item>
                    {/*
                      * Permissions and Notifications sit in tabs rather than one
                      * long column, so an administrator need not scroll past every
                      * permission to reach the notifications.
                      *
                      * forceRender mounts BOTH panes when the dialog opens, even
                      * the one not shown. That matters: a Form.Item only reports
                      * its value to onFinish (and to Form.useWatch) while it is
                      * mounted, so without it a role saved without visiting the
                      * Notifications tab would send no notifications and lose its
                      * subscriptions. The hidden pane is only display:none.
                      */}
                    <Tabs className="role-dialog-tabs" items={[
                        {
                            key: 'permissions',
                            // The count tells the administrator what is ticked without opening the tab.
                            label: 'Permissions (' + tickedClaims.length + ')',
                            forceRender: true,
                            children: (
                                <Form.Item name="claims" extra="Tick what people with this role may see and do.">
                                    <Checkbox.Group style={{ width: '100%' }} className="claim-groups">
                                        {byGroup.map(([group, list]) => {
                                            const expanded = openGroups.has(group);
                                            return (
                                                <div key={group} className={'claim-group' + (expanded ? ' is-open' : '')}>
                                                    <button type="button" className="claim-group-header" onClick={() => toggleGroup(group, setOpenGroups)}
                                                        aria-expanded={expanded}>
                                                        <span className="section-label">{group}</span>
                                                        <span className="claim-group-toggle">
                                                            {expanded ? 'Collapse' : 'Expand'}
                                                            {expanded ? <DownOutlined /> : <RightOutlined />}
                                                        </span>
                                                    </button>
                                                    <div className="claim-group-body" hidden={!expanded}>
                                                        {list.map((c) => (
                                                            <div key={c.claim} className="claim-option">
                                                                <Checkbox value={c.claim}>
                                                                    <Text strong>{c.label}</Text>
                                                                    {c.help && <div className="claim-help">{c.help}</div>}
                                                                    <div className="claim-code">{c.claim}</div>
                                                                </Checkbox>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </Checkbox.Group>
                                </Form.Item>
                            )
                        },
                        // No catalogue (a server older than 0024) means no notifications to offer, so no tab.
                        ...(notifications.length > 0 ? [{
                            key: 'notifications',
                            label: 'Notifications (' + tickedNotifications.length + ')',
                            forceRender: true,
                            children: (
                                <Form.Item name="notifications">
                                    <Checkbox.Group style={{ width: '100%' }} className="claim-groups">
                                        {offered.length === 0 && <Text type="secondary">Tick a permission to see the notifications it allows.</Text>}
                                        {offered.map(([group, list]) => {
                                            const expanded = openNotificationGroups.has(group);
                                            return (
                                                <div key={group} className={'claim-group' + (expanded ? ' is-open' : '')}>
                                                    <button type="button" className="claim-group-header" onClick={() => toggleGroup(group, setOpenNotificationGroups)}
                                                        aria-expanded={expanded}>
                                                        <span className="section-label">{group}</span>
                                                        <span className="claim-group-toggle">
                                                            {expanded ? 'Collapse' : 'Expand'}
                                                            {expanded ? <DownOutlined /> : <RightOutlined />}
                                                        </span>
                                                    </button>
                                                    <div className="claim-group-body" hidden={!expanded}>
                                                        {list.map((n) => (
                                                            <div key={n.code} className="claim-option">
                                                                <Checkbox value={n.code}>
                                                                    <Text strong>{n.label}</Text>
                                                                    {/* In-app always; email when the administrator setting says so. */}
                                                                    {n.forceEmail && <Tag style={{ marginInlineStart: 8 }}>email</Tag>}
                                                                    {!n.enabled && <Tag color="default">switched off</Tag>}
                                                                    {n.help && <div className="claim-help">{n.help}</div>}
                                                                    <div className="claim-code">{n.code}</div>
                                                                </Checkbox>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </Checkbox.Group>
                                </Form.Item>
                            )
                        }] : [])
                    ]} />
                </Form>
            </Modal>
        </section>
    );
};

export default RolesPanel;
